"use client";

import React, {
  useState,
  useEffect,
  startTransition,
  useCallback,
  useRef,
} from "react";
import styles from "./categoryModal.module.css";
import { HiOutlineXMark } from "react-icons/hi2";
import { MdOutlineCategory, MdDeleteOutline } from "react-icons/md";
import { useListingContext } from "../listingContex";
import { LocalPreviewMediaItem } from "./photosUploader";
import { useTranslations } from "next-intl";
import { UniqueIdentifier, useDroppable } from "@dnd-kit/core";
// Dnd-kit imports
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  MeasuringStrategy,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Image from "next/image";
import { getMedia } from "@/lib/media/shared";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

interface CategoryOrganizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  photos: LocalPreviewMediaItem[];
}

// Allowed Base Categories
export const BASE_ROOMS = [
  "exterior",
  "living_room",
  "kitchen",
  "dining_room",
  "bedroom",
  "bathroom",
  "leisure",
  "home_office",
  "balcony",
  "outdoors",
  "view",
  "other",
  "floor_plans",
];

const getPhotoId = (photo: LocalPreviewMediaItem): UniqueIdentifier => {
  const id =
    photo.MediaId || photo.uniqueKey || photo.assignedKey || photo.localUrl;

  if (!id) {
    return photo.OriginalFileName || "unknown-id";
  }

  return id;
};

export function CategoryOrganizerModal({
  isOpen,
  onClose,
  photos,
}: CategoryOrganizerModalProps) {
  const t = useTranslations("NewListing"); // Ensure this is mapped in your locales
  const { data, updateField } = useListingContext();

  // State: Maps Container IDs (e.g., "uncategorized", "bedroom-1") to an array of Photos
  const [items, setItems] = useState<Record<string, LocalPreviewMediaItem[]>>(
    {},
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedRoomToAdd, setSelectedRoomToAdd] = useState(BASE_ROOMS[0]);

  useDialogRouting("CategoryOrganizerModal", isOpen, onClose);

  // Initialize State on Open
  useEffect(() => {
    if (isOpen) {
      const initialItems: Record<string, LocalPreviewMediaItem[]> = {
        uncategorized: [],
      };

      photos.forEach((photo) => {
        const catKey = photo.room_tag; // e.g., "bedroom-1"
        if (catKey) {
          if (!initialItems[catKey]) initialItems[catKey] = [];
          initialItems[catKey].push(photo);
        } else {
          initialItems.uncategorized.push(photo);
        }
      });

      // Sort items within their buckets if room_order exists
      Object.keys(initialItems).forEach((key) => {
        initialItems[key].sort(
          (a, b) => (a.room_order || 0) - (b.room_order || 0),
        );
      });

      setItems(initialItems);
    }
  }, [isOpen]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (!isOpen) return null;

  // --- ACTIONS ---

  const handleAddRoom = () => {
    const base = selectedRoomToAdd;
    if (!items[base]) {
      setItems((prev) => ({ ...prev, [base]: [] }));
      return;
    }
    let index = 2;
    while (items[`${base}-${index}`]) {
      index++;
    }
    setItems((prev) => ({ ...prev, [`${base}-${index}`]: [] }));
  };

  const handleRemoveRoom = (roomKey: string) => {
    setItems((prev) => {
      const newItems = { ...prev };
      // Move all photos back to uncategorized
      newItems.uncategorized = [
        ...newItems.uncategorized,
        ...newItems[roomKey],
      ];
      delete newItems[roomKey];
      return newItems;
    });
  };

  const handleSave = () => {
    if (!data?.photos) return;

    // We MUST preserve the original keys ("0", "1", "2") for global sorting.
    // So we iterate over our local state, and update the matching items in the main context.
    const newPhotosState = { ...data.photos };

    Object.entries(items).forEach(([containerId, containerPhotos]) => {
      containerPhotos.forEach((photo, index) => {
        // Find this photo's original key in the global context
        const globalEntry = Object.entries(newPhotosState).find(
          ([_, p]) => p.MediaId === photo.MediaId,
        );
        if (globalEntry) {
          const [globalKey, globalItem] = globalEntry;

          if (containerId === "uncategorized") {
            newPhotosState[globalKey] = {
              ...globalItem,
              room_tag: undefined,
              room_order: undefined,
            };
          } else {
            // containerId is "bedroom-1" etc.
            newPhotosState[globalKey] = {
              ...globalItem,
              room_tag: containerId,
              room_order: index,
            };
          }
        }
      });
    });

    updateField("photos", newPhotosState);
    onClose();
  };

  // --- DND HANDLERS ---

  const findContainer = (id: string) => {
    if (id in items) return id;
    return Object.keys(items).find((key) =>
      items[key].find((item) => item.MediaId === id),
    );
  };

  const onDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    const activeContainer = findContainer(activeId);
    const overContainer =
      findContainer(overId) || (overId in items ? overId : null);

    // 1. EXIT EARLY: If we can't find containers or they are the same
    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }

    startTransition(() => {
      setItems((prev) => {
        const activeItems = prev[activeContainer];
        const overItems = prev[overContainer];

        const activeIndex = activeItems.findIndex(
          (i) => getPhotoId(i) === activeId,
        );
        if (activeIndex === -1) return prev;

        const overIndex =
          overId in prev
            ? overItems.length
            : overItems.findIndex((i) => getPhotoId(i) === overId);

        return {
          ...prev,
          [activeContainer]: activeItems.filter(
            (item) => getPhotoId(item) !== activeId,
          ),
          [overContainer]: [
            ...overItems.slice(0, overIndex),
            activeItems[activeIndex],
            ...overItems.slice(overIndex),
          ],
        };
      });
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const activeContainer = findContainer(active.id as string);
    const overContainer =
      findContainer(over.id as string) ||
      (over.id in items ? (over.id as string) : null);

    // Only re-sort if we are within the same container (onDragOver already handled cross-container moves)
    if (activeContainer && overContainer && activeContainer === overContainer) {
      const activeIndex = items[activeContainer].findIndex(
        (i) => getPhotoId(i) === active.id,
      );
      const overIndex = items[overContainer].findIndex(
        (i) => getPhotoId(i) === over.id,
      );

      // Make sure we aren't sorting against a container boundary (-1)
      if (activeIndex !== overIndex && overIndex !== -1) {
        setItems((prev) => ({
          ...prev,
          [overContainer]: arrayMove(
            prev[overContainer],
            activeIndex,
            overIndex,
          ),
        }));
      }
    }
  };

  const activeItem = activeId
    ? photos.find((p) => p.MediaId === activeId)
    : null;

  // Format Helper: "bedroom-1" -> "Bedroom 1" (Using Translations)
  const formatCategoryName = (key: string) => {
    if (key === "uncategorized") return "Uncategorized";
    const parts = key.split("-");
    const baseStr = t(`rooms.${parts[0]}`) || parts[0]; // e.g., t('rooms.bedroom') -> 'Bedroom'
    return parts.length > 1 ? `${baseStr} ${parts[1]}` : baseStr;
  };

  const sortedRoomKeys = Object.keys(items)
    .filter((k) => k !== "uncategorized")
    .sort((a, b) => {
      const baseA = a.split("-")[0];
      const baseB = b.split("-")[0];

      const idxA = BASE_ROOMS.indexOf(baseA);
      const idxB = BASE_ROOMS.indexOf(baseB);

      // 1. Sort by BASE_ROOMS order
      if (idxA !== idxB) return idxA - idxB;

      // 2. If same base room (e.g., bedroom-1 vs bedroom-2), sort by number
      const numA = parseInt(a.split("-")[1] || "1", 10);
      const numB = parseInt(b.split("-")[1] || "1", 10);
      return numA - numB;
    });

  return (
    <div
      className={styles.overlay}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.iconBox}>
              <MdOutlineCategory size={24} />
            </div>
            <div>
              <h2>Categorize Photos</h2>
              <p className={styles.description}>
                Drag photos into rooms to organize your buyer gallery.
              </p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className={styles.closeBtn}
          >
            <HiOutlineXMark size={24} />
          </button>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.BeforeDragging,
            },
          }}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
        >
          {/* TWO-PANE BODY */}
          <div className={styles.body}>
            {/* LEFT PANE: Uncategorized Pool */}
            <div className={styles.leftPane}>
              <div className={styles.paneHeader}>
                <h4 className={styles.paneTitle}>
                  Uncategorized ({items.uncategorized?.length || 0})
                </h4>
              </div>
              <div className={styles.scrollArea}>
                <DroppableSortableContainer
                  id="uncategorized"
                  items={items.uncategorized || []}
                />
              </div>
            </div>

            {/* RIGHT PANE: The Rooms */}
            <div className={styles.rightPane}>
              <div className={styles.paneHeader}>
                <h4 className={styles.paneTitle}>Your Rooms</h4>
                <div className={styles.addRoomWrapper}>
                  <select
                    className={styles.roomSelect}
                    value={selectedRoomToAdd}
                    onChange={(e) => setSelectedRoomToAdd(e.target.value)}
                  >
                    {BASE_ROOMS.map((room) => (
                      <option key={room} value={room}>
                        {t(`rooms.${room}`)}
                      </option>
                    ))}
                  </select>
                  <button className={styles.addRoomBtn} onClick={handleAddRoom}>
                    + Add
                  </button>
                </div>
              </div>

              <div className={styles.scrollArea}>
                {Object.keys(items).filter((k) => k !== "uncategorized")
                  .length === 0 && (
                  <div className={styles.emptyRoomBox}>
                    Add a room above to start organizing.
                  </div>
                )}
                {sortedRoomKeys.map((roomKey) => (
                  <div key={roomKey} className={styles.roomBucket}>
                    <div className={styles.roomBucketHeader}>
                      <span className={styles.roomName}>
                        {formatCategoryName(roomKey)}
                      </span>
                      <button
                        className={styles.closeBtn}
                        onClick={() => handleRemoveRoom(roomKey)}
                      >
                        <MdDeleteOutline size={18} />
                      </button>
                    </div>
                    <DroppableSortableContainer
                      id={roomKey}
                      items={items[roomKey]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DragOverlay>
            {activeItem ? (
              <DraggablePhotoCard photo={activeItem} isOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* FOOTER */}
        <div className={styles.footer}>
          <button
            className={styles.cancelBtn}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
          >
            Cancel
          </button>
          <button className={styles.submitBtn} onClick={handleSave}>
            Save Organization
          </button>
        </div>
      </div>
    </div>
  );
}

// --- SUB-COMPONENTS FOR DND ---

function DroppableSortableContainer({
  id,
  items,
}: {
  id: string;
  items: LocalPreviewMediaItem[];
}) {
  const { setNodeRef, isOver } = useDroppable({ id });

  const itemIds = React.useMemo(() => items.map(getPhotoId), [items]);

  return (
    <SortableContext id={id} items={itemIds} strategy={rectSortingStrategy}>
      <div
        ref={setNodeRef}
        className={styles.photoGrid}
        style={{
          minHeight: "120px",
          backgroundColor: "transparent",
        }}
      >
        {items.map((photo) => (
          <SortablePhoto key={getPhotoId(photo)} photo={photo} />
        ))}
      </div>
    </SortableContext>
  );
}

function SortablePhoto({ photo }: { photo: LocalPreviewMediaItem }) {
  const safeId = getPhotoId(photo);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: safeId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition, // Let dnd-kit handle the transition, don't override it in CSS
    opacity: isDragging ? 0.4 : 1,
    touchAction: "none", // 🚀 CRITICAL: Stops the browser from interrupting the drag
    zIndex: isDragging ? 99 : 1,
    willChange: isDragging ? "transform" : "auto",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <DraggablePhotoCard photo={photo} />
    </div>
  );
}

function DraggablePhotoCard({
  photo,
  isOverlay,
}: {
  photo: LocalPreviewMediaItem;
  isOverlay?: boolean;
}) {
  const resolvedMedia = getMedia(photo, "sm");
  const displayUrl = photo.localUrl || resolvedMedia?.src || "";
  const isBlob = displayUrl.startsWith("blob:");

  return (
    <div
      className={styles.draggablePhoto}
      style={{
        boxShadow: isOverlay ? "0 10px 20px rgba(0,0,0,0.2)" : "none",
        cursor: isOverlay ? "grabbing" : "grab",
      }}
    >
      {displayUrl && (
        <Image
          src={displayUrl}
          alt="Room Photo"
          fill
          sizes="150px"
          unoptimized={isBlob}
          style={{ pointerEvents: "none" }}
        />
      )}
    </div>
  );
}
